import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Node,
  Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { DataFrameAnalysisProto } from "build-ts/dataframe";
import { getArtifactType, getArtifactHeaderName } from "modules/artifacts/utils/artifactUtils";
import { getParentPillIcon, RESULT_PILL_KIND } from "modules/artifacts/dataframe/utils/dataFrameCodeUtils";
import { AppIcons } from "constants/icons";

/**
 * Trace tab: interactive lineage DAG for the open dataframe_analysis artifact.
 *
 * Pipeline (each time `data` or `lookupFn` changes):
 *   1. buildLineageGraph  — DFS over analyses via lookupFn; dedupe nodes/edges by id
 *   2. applyDagreLayout   — dagre TB layout, then wrapCrowdedRanks for wide levels
 *   3. addBoundaryNode    — dashed box around nodes with isCurrent=true
 *   4. React Flow render  — fitBounds on current scope; dynamic minZoom from graph size
 *
 * Node kinds (React Flow `type` must not be "output" — that is a built-in RF type):
 *   kgSource   — terminal KG_DATAFRAME input
 *   dataframe  — table sub-resource (upstream output or current input); matches RESULT_PILL_KIND.dataframe
 *   chart      — chart sub-resource; matches RESULT_PILL_KIND.chart
 *   report     — report/document sub-resource; matches RESULT_PILL_KIND.report
 *   code       — Python step for an analysis
 *   result     — current analysis deliverables (promoted from dataframe/chart/docx)
 *   boundary   — non-interactive scope rectangle (zIndex behind content)
 *
 * Shared ancestors appear once: node ids are stable (`df:{name}`, `code:{analysisName}`, `kg:{name}`).
 */

// ── Constants (tune layout/zoom in one place) ─────────────────────────────────

const NODE_W = 270;
const NODE_H = 64;
const BOUNDARY_PAD = 28;
const MAX_DEPTH = 10;
/** Max nodes on one dagre rank before wrapping into additional sub-rows */
const MAX_NODES_PER_ROW = 2;
const RANK_ROW_GAP = 16;
const RANK_Y_TOLERANCE = 10;
const RANK_COL_GAP = 20;

const GRAPH_MIN_ZOOM = 0.25;
const GRAPH_MAX_ZOOM = 1.5;
const EDGE_DEFAULT = "#9ca3af";
const EDGE_ACTIVE = "#7c3aed";
const EDGE_DIM = "#e5e7eb";

// ── Types ────────────────────────────────────────────────────────────────────

// Sub-resource kinds mirror RESULT_PILL_KIND values so routing and rendering use the same constants.
type LineageKind = "kgSource" | "fileSource" | "dataframe" | "chart" | "report" | "code" | "result" | "boundary";

// ResultPillKind is the string union of RESULT_PILL_KIND values; used for outputKind on result nodes.
type ResultPillKind = (typeof RESULT_PILL_KIND)[keyof typeof RESULT_PILL_KIND];

interface LineageNodeData extends Record<string, unknown> {
  kind: LineageKind;
  label: string;
  isCurrent: boolean;
  icon?: React.ElementType;
  artifact?: ArtifactProto;
  subResourceName?: string;
  // Only populated on result-kind nodes (current artifact's deliverables).
  outputKind?: ResultPillKind;
  /** Index in data.parent_headers; only set for current artifact's direct inputs */
  phIndex?: number;
}

interface LineageGraphTabProps {
  data: DataFrameAnalysisProto;
  lookupFn?: (name: string) => ArtifactProto | undefined;
  // Single navigation callback — parent owns all routing logic.
  //  Receives the sub-resource name (chart/df/report/ph name) or the artifact's
  //  header name for external artifacts. Parent resolves the right tab.
  onNavigate: (name: string, kind: LineageKind) => void;
  // True when the Trace tab is currently selected. Used to defer fitBounds until
  //  the container has real dimensions (it is display:none while inactive).
  isActive: boolean;
}

// ── Graph builder ────────────────────────────────────────────────────────────
//
// How lineage is tracked (no separate graph DB — we derive edges from proto fields):
//
// Each dataframe_analysis artifact already records what it consumed and produced:
//   - parent_headers[]     → inputs (name = a table/chart/report the agent passed in)
//   - pandas_dataframes[], charts[], reports[] → outputs of that analysis's Python run
//
// lookupFn(name) resolves a parent_header name to the owning ArtifactProto in the session
// (same store InputsTab uses). That tells us whether the input is KG_DATAFRAME or another
// DATAFRAME_ANALYSIS, and which upstream analysis to walk next.
//
// For ONE analysis, processAnalysis() adds this repeating shape:
//
//     [inputs…] ──► code:{analysisName} ──► [df:{outputName}, …]
//        ▲              (Python step)         (published results)
//        │
//   kg:{table}  OR  df:{parent_header.name}
//
// - KG input: one kg node, edge into code, stop (warehouse leaf).
// - Analysis input: node df:{ph.name} (the specific result used), edge into code, then
//   processAnalysis(inputArtifact) so that upstream analysis's code/outputs appear above.
//
// Node ids are global in the session graph:
//   code:{header.name}  — one per analysis (visited set skips re-entry)
//   df:{resource.name}  — one per table/chart/report name (shared if two analyses use same df)
//   kg:{kg header name}
//
// We start at the open artifact (data), DFS upstream via parent_headers, then post-process:
// mark isCurrent on its code/inputs/outputs, promote outputs to kind "result", prune dead-end
// upstream dataframes that nothing downstream references.

function buildLineageGraph(
  data: DataFrameAnalysisProto,
  lookupFn: (name: string) => ArtifactProto | undefined
): { nodes: Node<LineageNodeData>[]; edges: Edge[] } {
  const nodeMap = new Map<string, Node<LineageNodeData>>();
  const edgeMap = new Map<string, Edge>();
  const visited = new Set<string>();

  /** Icons match InputsTab pills — see getParentPillIcon in dataFrameCodeUtils. */
  function resolveIcon(name: string | undefined, kind: LineageKind): React.ElementType | undefined {
    if (kind === "code") return AppIcons.python;
    if (kind === "boundary") return undefined;
    return getParentPillIcon(name, lookupFn);
  }

  /** Returns the RESULT_PILL_KIND value for a sub-resource name by inspecting its parent artifact. */
  function outputKindForName(name: string | undefined): Extract<ResultPillKind, LineageKind> {
    const artifact = lookupFn(name ?? "");
    const dfa = artifact?.dataframe_analysis;
    if ((dfa?.charts ?? []).some(c => c.name === name)) return RESULT_PILL_KIND.chart;
    if ((dfa?.reports ?? []).some(r => r.name === name)) return RESULT_PILL_KIND.report;
    return RESULT_PILL_KIND.dataframe;
  }

  /** First write wins — same id from parent_headers + processAnalysis must not overwrite. */
  function addNode(id: string, nodeData: LineageNodeData): void {
    if (!nodeMap.has(id)) {
      const icon = nodeData.icon ?? resolveIcon(nodeData.subResourceName ?? nodeData.label, nodeData.kind);
      nodeMap.set(id, {
        id,
        type: nodeData.kind,
        position: { x: 0, y: 0 },
        data: { ...nodeData, icon },
        // Transparent wrapper avoids React Flow's default node box showing behind custom nodes
        style: nodeData.kind !== "boundary" ? { padding: 0, background: "transparent" } : undefined,
        draggable: false,
        selectable: nodeData.kind !== "boundary",
      });
    }
  }

  function addEdge(source: string, target: string): void {
    const id = `${source}::${target}`;
    if (!edgeMap.has(id)) {
      edgeMap.set(id, {
        id,
        source,
        target,
        type: "smoothstep",
        style: { stroke: EDGE_DEFAULT, strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_DEFAULT, width: 10, height: 10 },
      });
    }
  }

  /**
   * Walk one analysis: code node, its published outputs (df/chart/report), then inputs.
   * - KG parent → kg node → code
   * - Analysis parent → df:{phName} edge into code, then recurse upstream
   * visited set prevents cycles; shared upstream nodes are created once.
   */
  function processAnalysis(artifact: ArtifactProto, depth: number): void {
    if (depth > MAX_DEPTH) return;
    const dfa = artifact.dataframe_analysis;
    if (!dfa) return;
    const analysisName = getArtifactHeaderName(artifact);
    if (!analysisName || visited.has(analysisName)) return;
    visited.add(analysisName);

    const codeNodeId = `code:${analysisName}`;
    addNode(codeNodeId, { kind: "code", label: dfa.header?.name ?? analysisName, isCurrent: false, artifact });

    for (const df of dfa.pandas_dataframes ?? []) {
      if (!df.name) continue;
      addNode(`df:${df.name}`, {
        kind: RESULT_PILL_KIND.dataframe,
        label: df.name,
        isCurrent: false,
        artifact,
        subResourceName: df.name,
      });
      addEdge(codeNodeId, `df:${df.name}`);
    }
    for (const chart of dfa.charts ?? []) {
      if (!chart.name) continue;
      addNode(`df:${chart.name}`, {
        kind: RESULT_PILL_KIND.chart,
        label: chart.name,
        isCurrent: false,
        artifact,
        subResourceName: chart.name,
      });
      addEdge(codeNodeId, `df:${chart.name}`);
    }
    for (const report of dfa.reports ?? []) {
      if (!report.name) continue;
      addNode(`df:${report.name}`, {
        kind: RESULT_PILL_KIND.report,
        label: report.name,
        isCurrent: false,
        artifact,
        subResourceName: report.name,
      });
      addEdge(codeNodeId, `df:${report.name}`);
    }

    for (const ph of dfa.parent_headers ?? []) {
      const phName = ph.name ?? "";
      if (!phName) continue;
      const inputArtifact = lookupFn(phName);
      if (!inputArtifact) continue;
      const inputType = getArtifactType(inputArtifact);

      if (inputType === ArtifactType.KG_DATAFRAME) {
        const kgId = `kg:${getArtifactHeaderName(inputArtifact) || phName}`;
        addNode(kgId, { kind: "kgSource", label: phName, isCurrent: false, artifact: inputArtifact });
        addEdge(kgId, codeNodeId);
      } else if (inputType === ArtifactType.DATAFRAME_ANALYSIS) {
        addNode(`df:${phName}`, {
          kind: outputKindForName(phName),
          label: phName,
          isCurrent: false,
          artifact: inputArtifact,
          subResourceName: phName,
        });
        addEdge(`df:${phName}`, codeNodeId);
        processAnalysis(inputArtifact, depth + 1);
      } else if (inputType === ArtifactType.FILE_ARTIFACT) {
        const fileId = `file:${getArtifactHeaderName(inputArtifact) || phName}`;
        addNode(fileId, { kind: "fileSource", label: phName, isCurrent: false, artifact: inputArtifact });
        addEdge(fileId, codeNodeId);
      }
    }
  }

  // Pass 1: full tree, nothing highlighted yet
  const currentArtifact = lookupFn(data.header?.name ?? "") ?? ({ dataframe_analysis: data } as ArtifactProto);
  processAnalysis(currentArtifact, 0);

  // Pass 2: highlight the open artifact (code, inputs, outputs)
  const currentName = data.header?.name ?? "";
  const currentCodeId = `code:${currentName}`;
  if (nodeMap.has(currentCodeId)) {
    const n = nodeMap.get(currentCodeId)!;
    nodeMap.set(currentCodeId, { ...n, data: { ...n.data, isCurrent: true } });
  }

  // Current deliverables use kind "result" (distinct styling + click → Results tab).
  // outputKind records the sub-type (RESULT_PILL_KIND value) for OutputNode's sub-label.
  function promoteToOutput(dfName: string, outputKind: ResultPillKind): void {
    const nodeId = `df:${dfName}`;
    if (!nodeMap.has(nodeId)) return;
    const n = nodeMap.get(nodeId)!;
    nodeMap.set(nodeId, { ...n, type: "result", data: { ...n.data, kind: "result", isCurrent: true, outputKind } });
  }
  (data.pandas_dataframes ?? []).forEach(df => df.name && promoteToOutput(df.name, RESULT_PILL_KIND.dataframe));
  (data.charts ?? []).forEach(c => c.name && promoteToOutput(c.name, RESULT_PILL_KIND.chart));
  (data.reports ?? []).forEach(r => r.name && promoteToOutput(r.name, RESULT_PILL_KIND.report));

  // Mark current artifact's direct inputs as isCurrent=true and attach phIndex
  (data.parent_headers ?? []).forEach((ph, phIdx) => {
    if (!ph.name) return;
    const nodeId = `df:${ph.name}`;
    if (!nodeMap.has(nodeId)) return;
    const n = nodeMap.get(nodeId)!;
    nodeMap.set(nodeId, { ...n, data: { ...n.data, isCurrent: true, phIndex: phIdx } });
  });

  // Drop upstream dataframe nodes with no outgoing edge (not used by any downstream analysis)
  const sourcedIds = new Set(Array.from(edgeMap.values()).map(e => e.source));
  const pruned = new Set<string>();
  const SUB_RESOURCE_KINDS = new Set<string>([
    RESULT_PILL_KIND.dataframe,
    RESULT_PILL_KIND.chart,
    RESULT_PILL_KIND.report,
  ]);
  nodeMap.forEach((n, id) => {
    if (SUB_RESOURCE_KINDS.has(n.data.kind) && !n.data.isCurrent && !sourcedIds.has(id)) {
      pruned.add(id);
      nodeMap.delete(id);
    }
  });
  if (pruned.size > 0) {
    edgeMap.forEach((e, eid) => {
      if (pruned.has(e.source) || pruned.has(e.target)) edgeMap.delete(eid);
    });
  }

  return { nodes: Array.from(nodeMap.values()), edges: Array.from(edgeMap.values()) };
}

// ── Dagre layout ─────────────────────────────────────────────────────────────
//
// dagre assigns one horizontal row per rank (same y). wrapCrowdedRanks splits busy
// rows into sub-rows (MAX_NODES_PER_ROW) and pushes lower ranks down via pushDown.

function groupNodesByRank(nodes: Node<LineageNodeData>[]): Node<LineageNodeData>[][] {
  const sorted = [...nodes].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
  if (sorted.length === 0) return [];

  const groups: Node<LineageNodeData>[][] = [];
  let current = [sorted[0]];
  let refY = sorted[0].position.y;

  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].position.y - refY) <= RANK_Y_TOLERANCE) {
      current.push(sorted[i]);
    } else {
      groups.push(current);
      current = [sorted[i]];
      refY = sorted[i].position.y;
    }
  }
  groups.push(current);
  return groups;
}

/**
 * Post-process dagre positions: if a rank has more than MAX_NODES_PER_ROW nodes,
 * lay them out in a centered grid (e.g. 5 nodes → 2+2+1 with MAX=2) instead of
 * one very wide row. pushDown accumulates extra height for ranks below.
 */
function wrapCrowdedRanks(nodes: Node<LineageNodeData>[]): Node<LineageNodeData>[] {
  const content = nodes.filter(n => n.data.kind !== "boundary");
  if (content.length === 0) return nodes;

  const rankGroups = groupNodesByRank(content);
  const positionById = new Map<string, { x: number; y: number }>();
  let pushDown = 0;

  for (const group of rankGroups) {
    const sorted = [...group].sort((a, b) => a.position.x - b.position.x);
    const baseY = sorted[0].position.y + pushDown;
    const centerX = sorted.reduce((sum, n) => sum + n.position.x + NODE_W / 2, 0) / sorted.length;

    if (sorted.length <= MAX_NODES_PER_ROW) {
      sorted.forEach(n => positionById.set(n.id, { x: n.position.x, y: baseY }));
      continue;
    }

    const numRows = Math.ceil(sorted.length / MAX_NODES_PER_ROW);
    const rowStride = NODE_H + RANK_ROW_GAP;
    pushDown += (numRows - 1) * rowStride;

    for (let row = 0; row < numRows; row++) {
      const rowNodes = sorted.slice(row * MAX_NODES_PER_ROW, (row + 1) * MAX_NODES_PER_ROW);
      const rowWidth = rowNodes.length * NODE_W + (rowNodes.length - 1) * RANK_COL_GAP;
      let x = centerX - rowWidth / 2;
      const y = baseY + row * rowStride;
      for (const n of rowNodes) {
        positionById.set(n.id, { x, y });
        x += NODE_W + RANK_COL_GAP;
      }
    }
  }

  return nodes.map(n => {
    if (n.data.kind === "boundary") return n;
    const pos = positionById.get(n.id);
    return pos ? { ...n, position: pos } : n;
  });
}

function applyDagreLayout(nodes: Node<LineageNodeData>[], edges: Edge[]): Node<LineageNodeData>[] {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 50, nodesep: RANK_COL_GAP, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));

  const contentNodes = nodes.filter(n => n.data.kind !== "boundary");
  contentNodes.forEach(n => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach(e => {
    if (g.hasNode(e.source) && g.hasNode(e.target)) g.setEdge(e.source, e.target);
  });

  dagre.layout(g);

  const laidOut = nodes.map(n => {
    if (n.data.kind === "boundary") return n;
    const gn = g.node(n.id);
    if (!gn) return n;
    return { ...n, position: { x: gn.x - NODE_W / 2, y: gn.y - NODE_H / 2 } };
  });

  return wrapCrowdedRanks(laidOut);
}

// ── Boundary node ────────────────────────────────────────────────────────────
//
// Sized from the bounding box of isCurrent content nodes; rendered behind them (zIndex -1).

function addBoundaryNode(nodes: Node<LineageNodeData>[]): Node<LineageNodeData>[] {
  const currentNodes = nodes.filter(n => n.data.isCurrent && n.data.kind !== "boundary");
  if (currentNodes.length === 0) return nodes;

  const xs = currentNodes.map(n => n.position.x);
  const ys = currentNodes.map(n => n.position.y);
  const minX = Math.min(...xs) - BOUNDARY_PAD;
  const minY = Math.min(...ys) - BOUNDARY_PAD;
  const maxX = Math.max(...xs) + NODE_W + BOUNDARY_PAD;
  const maxY = Math.max(...ys) + NODE_H + BOUNDARY_PAD;

  const boundaryNode: Node<LineageNodeData> = {
    id: "boundary",
    type: "boundary",
    position: { x: minX, y: minY },
    style: { width: maxX - minX, height: maxY - minY, zIndex: -1, pointerEvents: "none" },
    data: { kind: "boundary", label: "Current Analysis", isCurrent: true },
    selectable: false,
    draggable: false,
    zIndex: -1,
  };

  return [boundaryNode, ...nodes];
}

// ── Custom node components ────────────────────────────────────────────────────
//
// Chakra HStack/Box inside React Flow node types. Handles are invisible placeholders
// for edge attachment (top target / bottom source in TB layout).

const HANDLE_STYLE: React.CSSProperties = { background: "transparent", border: "none", width: 0, height: 0 };

const NODE_BOX_BASE = {
  w: `${NODE_W}px`,
  h: `${NODE_H}px`,
  borderRadius: "lg",
  px: 3,
  gap: 2,
  cursor: "pointer",
  transition: "box-shadow 0.15s",
  overflow: "hidden",
} as const;

const NodeLabel: React.FC<{ label: string; sub: string }> = ({ label, sub }) => (
  <Box minW={0} flex={1} overflow="hidden">
    <Text
      fontSize="xs"
      fontWeight={600}
      color="gray.800"
      lineHeight="short"
      mb={0}
      noOfLines={2}
      textAlign="left"
      wordBreak="break-word"
    >
      {label}
    </Text>
    <Text textAlign="left" fontSize="xs" color="gray.500" lineHeight="short" mb={0} noOfLines={1}>
      {sub}
    </Text>
  </Box>
);

/** Sub-label for all sub-resource node kinds — keyed by RESULT_PILL_KIND values. */
const NODE_KIND_LABEL: Record<string, string> = {
  [RESULT_PILL_KIND.dataframe]: "Table",
  [RESULT_PILL_KIND.chart]: "Chart",
  [RESULT_PILL_KIND.report]: "Document",
};

const KgSourceNode: React.FC<{ data: LineageNodeData }> = ({ data }) => (
  <HStack {...NODE_BOX_BASE} bg="gray.50" border="1.5px solid" borderColor="gray.300">
    <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
    <Icon as={data.icon ?? AppIcons.database} boxSize={5} color="gray.400" flexShrink={0} />
    <NodeLabel label={data.label} sub="Source table" />
  </HStack>
);

const FileSourceNode: React.FC<{ data: LineageNodeData }> = ({ data }) => (
  <HStack {...NODE_BOX_BASE} bg="gray.50" border="1.5px solid" borderColor="gray.300">
    <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
    <Icon as={data.icon ?? AppIcons.file} boxSize={5} color="gray.400" flexShrink={0} />
    <NodeLabel label={data.label} sub="Uploaded file" />
  </HStack>
);

const DataframeNode: React.FC<{ data: LineageNodeData }> = ({ data }) => {
  const sub = NODE_KIND_LABEL[data.kind] ?? "Table";
  return (
    <HStack
      {...NODE_BOX_BASE}
      bg={data.isCurrent ? "purple.50" : "white"}
      border={data.isCurrent ? "2px solid" : "1.5px solid"}
      borderColor={data.isCurrent ? "purple.600" : "gray.400"}
    >
      <Handle type="target" position={Position.Top} style={HANDLE_STYLE} />
      <Icon
        as={data.icon ?? AppIcons.dataframe}
        boxSize={5}
        color={data.isCurrent ? "purple.600" : "gray.400"}
        flexShrink={0}
      />
      <NodeLabel label={data.label} sub={sub} />
      <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
    </HStack>
  );
};

const CodeNode: React.FC<{ data: LineageNodeData }> = ({ data }) => (
  <HStack
    {...NODE_BOX_BASE}
    bg={data.isCurrent ? "purple.50" : "white"}
    border={data.isCurrent ? "2px solid" : "1.5px solid"}
    borderColor={data.isCurrent ? "purple.600" : "gray.400"}
    boxShadow={data.isCurrent ? "0 0 0 3px rgba(124,58,237,0.12)" : undefined}
  >
    <Handle type="target" position={Position.Top} style={HANDLE_STYLE} />
    <Icon
      as={data.icon ?? AppIcons.python}
      boxSize={5}
      color={data.isCurrent ? "purple.600" : "gray.400"}
      flexShrink={0}
    />
    <NodeLabel label={data.label} sub={data.isCurrent ? "Python code" : "Python code"} />
    <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
  </HStack>
);

const OutputNode: React.FC<{ data: LineageNodeData }> = ({ data }) => {
  // outputKind is a RESULT_PILL_KIND value set by promoteToOutput; NODE_KIND_LABEL provides the label.
  const sub = NODE_KIND_LABEL[data.outputKind ?? RESULT_PILL_KIND.dataframe] ?? "Table";
  return (
    <HStack
      {...NODE_BOX_BASE}
      bg="purple.50"
      border="2px solid"
      borderColor="purple.700"
      boxShadow="0 2px 10px rgba(91,33,182,0.18)"
    >
      <Handle type="target" position={Position.Top} style={HANDLE_STYLE} />
      <Icon as={data.icon ?? AppIcons.dataframe} boxSize={5} color="purple.700" flexShrink={0} />
      <NodeLabel label={data.label} sub={sub} />
    </HStack>
  );
};

const BoundaryNode: React.FC<{ data: LineageNodeData }> = () => (
  <Box
    w="100%"
    h="100%"
    border="2px dashed"
    borderColor="purple.200"
    borderRadius="xl"
    bg="rgba(245,243,255,0.2)"
    pointerEvents="none"
    position="relative"
  >
    <Box
      position="absolute"
      top="-11px"
      left="14px"
      bg="purple.50"
      border="1px solid"
      borderColor="purple.200"
      borderRadius="sm"
      px={2}
      py={0}
    >
      <Text
        fontSize="0.58rem"
        fontWeight={700}
        color="purple.600"
        letterSpacing="0.06em"
        textTransform="uppercase"
        lineHeight={1.6}
        mb={0}
      >
        Current Analysis
      </Text>
    </Box>
  </Box>
);

const nodeTypes = {
  kgSource: KgSourceNode,
  fileSource: FileSourceNode,
  dataframe: DataframeNode,
  chart: DataframeNode, // same visual as dataframe; kind drives the sub-label
  report: DataframeNode, // same visual as dataframe; kind drives the sub-label
  code: CodeNode,
  result: OutputNode,
  boundary: BoundaryNode,
};

// ── Main component ────────────────────────────────────────────────────────────
//
// LineageGraphTabInner must run inside ReactFlowProvider (see export below).
// Initial view: fitBounds on isCurrent nodes only — pan up to see upstream lineage.

const LineageGraphTabInner: React.FC<LineageGraphTabProps> = ({ data, lookupFn, onNavigate, isActive }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<LineageNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const rawEdgesRef = useRef<Edge[]>([]);
  const positionedNodesRef = useRef<Node<LineageNodeData>[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dynamicMinZoom, setDynamicMinZoom] = useState(GRAPH_MIN_ZOOM);
  const { fitBounds } = useReactFlow();
  const hoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // fitDoneRef: true once the initial viewport has been set. After that, the user's
  // pan/zoom is preserved on subsequent tab switches (we never re-fit).
  const fitDoneRef = useRef(false);
  // graphBuiltRef: true once nodes/edges have been loaded into React Flow state.
  const graphBuiltRef = useRef(false);

  // Build and lay out the graph whenever data changes. Does NOT call fitBounds here
  // because the container may be display:none (zero dimensions) at this point.
  useEffect(() => {
    const { nodes: rawNodes, edges: rawEdges } = buildLineageGraph(data, lookupFn ?? (() => undefined));
    const positioned = applyDagreLayout(rawNodes, rawEdges);
    const withBoundary = addBoundaryNode(positioned);

    const styledEdges = rawEdges.map(e => ({
      ...e,
      style: { stroke: EDGE_DEFAULT, strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_DEFAULT, width: 10, height: 10 },
    }));
    rawEdgesRef.current = styledEdges;
    positionedNodesRef.current = positioned;

    // minZoom = zoom at which entire graph fits container (floor: GRAPH_MIN_ZOOM).
    // Falls back to default constants when container is hidden (offsetWidth/Height = 0).
    const contentNodes = positioned.filter(n => n.data.kind !== "boundary");
    if (contentNodes.length > 0) {
      const cW = containerRef.current?.offsetWidth ?? 500;
      const cH = containerRef.current?.offsetHeight ?? 400;
      const xs = contentNodes.map(n => n.position.x);
      const ys = contentNodes.map(n => n.position.y);
      const gW = Math.max(...xs) + NODE_W - Math.min(...xs);
      const gH = Math.max(...ys) + NODE_H - Math.min(...ys);
      const fitZoom = Math.min(cW / gW, cH / gH) * 0.85;
      setDynamicMinZoom(Math.max(GRAPH_MIN_ZOOM, Math.min(fitZoom, GRAPH_MAX_ZOOM)));
    }

    setNodes(withBoundary as any);
    setEdges(styledEdges as any);
    graphBuiltRef.current = true;
    // If the tab is already visible when data loads (e.g. user is on Trace tab and
    // data updates), reset fitDone so the viewport re-centres on the current analysis.
    if (isActive) fitDoneRef.current = false;
  }, [data, lookupFn, isActive]);

  // Deferred fitBounds — fires the first time the Trace tab becomes visible after
  // the graph is built. Uses rAF + setTimeout to ensure React Flow has painted nodes
  // before measuring. After the first fit, viewport is preserved (fitDoneRef stays true).
  useEffect(() => {
    if (!isActive || fitDoneRef.current || !graphBuiltRef.current) return;
    requestAnimationFrame(() => {
      setTimeout(() => {
        const currentNodes = positionedNodesRef.current.filter(n => n.data.isCurrent && n.data.kind !== "boundary");
        if (currentNodes.length === 0) return;
        const xs = currentNodes.map(n => n.position.x);
        const ys = currentNodes.map(n => n.position.y);
        fitBounds(
          {
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) + NODE_W - Math.min(...xs),
            height: Math.max(...ys) + NODE_H - Math.min(...ys),
          },
          { padding: 0.35, duration: 0 }
        );
        fitDoneRef.current = true;
      }, 50);
    });
  }, [isActive, fitBounds]);

  // Navigate on node click — always pass the most specific name available.
  //
  // Why a single name is sufficient (parent owns all routing logic):
  //   current code node     → label = artifact header name  → parent matches data.header?.name → code tab
  //   current result node   → subResourceName = pill name   → parent matches resultPills       → results tab
  //   current input node    → subResourceName = ph.name     → parent matches parent_headers    → inputs tab
  //   upstream dataframe    → subResourceName = ph.name     → parent matches parent_headers    → inputs tab
  //   KG source / upstream  → label = artifact header name  → parent calls lookupFn + opens artifact
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const d = node.data as LineageNodeData;
      onNavigate(d.subResourceName ?? d.label, d.kind);
    },
    [onNavigate]
  );

  // Highlight incident edges; dim everything else (restore on leave via rawEdgesRef)
  const onNodeMouseEnter = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
      const connectedIds = new Set<string>([node.id]);
      rawEdgesRef.current.forEach(e => {
        if (e.source === node.id) connectedIds.add(e.target);
        if (e.target === node.id) connectedIds.add(e.source);
      });
      setNodes(
        prev =>
          prev.map(n => ({
            ...n,
            style: { ...n.style, opacity: n.data.kind === "boundary" ? 0.4 : connectedIds.has(n.id) ? 1 : 0.22 },
          })) as any
      );
      setEdges(
        prev =>
          prev.map(e => {
            const active = e.source === node.id || e.target === node.id;
            return {
              ...e,
              style: { stroke: active ? EDGE_ACTIVE : EDGE_DIM, strokeWidth: active ? 2 : 1 },
              markerEnd: active
                ? { type: MarkerType.ArrowClosed, color: EDGE_ACTIVE, width: 10, height: 10 }
                : undefined,
            };
          }) as any
      );
    },
    [setNodes, setEdges]
  );

  const onNodeMouseLeave = useCallback(() => {
    hoverLeaveTimerRef.current = setTimeout(() => {
      hoverLeaveTimerRef.current = null;
      setNodes(prev => prev.map(n => ({ ...n, style: { ...n.style, opacity: 1 } })) as any);
      setEdges(rawEdgesRef.current as any);
    }, 60);
  }, [setNodes, setEdges]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        nodeTypes={nodeTypes as any}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        elementsSelectable={false}
        minZoom={dynamicMinZoom}
        maxZoom={GRAPH_MAX_ZOOM}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
        <Controls
          position="top-right"
          showInteractive={false}
          style={{ marginTop: 12, marginRight: 12, display: "flex", flexDirection: "row", gap: 4 }}
        />
      </ReactFlow>
    </div>
  );
};

// ── Public export ─────────────────────────────────────────────────────────────
// ReactFlowProvider is required for useReactFlow / fitBounds in the inner component.
// React.memo prevents re-renders when the parent updates for unrelated reasons
// (e.g. pill selection, tab switches) — the graph only re-renders when data or
// a callback prop actually changes.

const LineageGraphTab: React.FC<LineageGraphTabProps> = React.memo(props => (
  <ReactFlowProvider>
    <LineageGraphTabInner {...props} />
  </ReactFlowProvider>
));

export default LineageGraphTab;
